import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';

import App from './App';
import { AuthContextProvider } from './contexts/AuthContext';
import { SettingsContextProvider } from './contexts/SettingsContext';
import { UserProfileContextProvider } from './contexts/UserProfileContext';
import { IntegrationsContextProvider } from './contexts/IntegrationsContext';
import { ServerRecognitionContextProvider } from './contexts/ServerRecognitionContext';
import { LocalRecognitionContextProvider } from './contexts/LocalRecognitionContext';
import { InstallAppContextProvider } from './contexts/InstallAppContext';
import { ServerEventsContextProvider } from './contexts/ServerEventsContext';
import { FeatureFlagsContextProvider } from './contexts/FeatureFlagsContext';
import { ConsentsContextProvider } from './contexts/ConsentsContext';
import { StyledSnackbarProvider } from './components/StyledSnackbarProvider';
import store from 'src/redux/store';
import handleQueryParams from 'src/lib/handleQueryParams';
import { EventHandlerProvider } from './contexts/EventHandlerContext';
import { AcapellaProvider } from './contexts/AcapellaContext';

// Dev-only accessibility auditing: logs WCAG violations to the console on every
// render. Never bundled in production builds.
if (process.env.NODE_ENV === 'development') {
  Promise.all([import('react-dom'), import('@axe-core/react')]).then(
    ([reactDom, axe]) => {
      axe.default(React, reactDom.default ?? reactDom, 1000);
    },
  );
}

const render = () => {
  const { shouldRenderApp } = handleQueryParams();
  if (!shouldRenderApp) {
    return;
  }

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <BrowserRouter>
      <StyledSnackbarProvider dense maxSnack={3}>
        <ReduxProvider store={store}>
          <EventHandlerProvider>
            <AuthContextProvider>
              <SettingsContextProvider>
                <IntegrationsContextProvider>
                  <ServerRecognitionContextProvider>
                    <LocalRecognitionContextProvider>
                      <UserProfileContextProvider>
                        <FeatureFlagsContextProvider>
                          <AcapellaProvider>
                            <ServerEventsContextProvider>
                              <InstallAppContextProvider>
                                <ConsentsContextProvider>
                                  <App />
                                </ConsentsContextProvider>
                              </InstallAppContextProvider>
                            </ServerEventsContextProvider>
                          </AcapellaProvider>
                        </FeatureFlagsContextProvider>
                      </UserProfileContextProvider>
                    </LocalRecognitionContextProvider>
                  </ServerRecognitionContextProvider>
                </IntegrationsContextProvider>
              </SettingsContextProvider>
            </AuthContextProvider>
          </EventHandlerProvider>
        </ReduxProvider>
      </StyledSnackbarProvider>
    </BrowserRouter>,
  );
};

render();
